import { useState, useEffect, useRef } from 'react'
import { Upload, FileText, FileSpreadsheet, FileImage, Trash2, Download, Loader2, FolderOpen, File } from 'lucide-react'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'

const SURFACE = '#FFFFFF'
const BG      = '#F4F7FC'
const BORDER  = '#DDE4EF'
const TEXT    = '#0C1629'
const MUTED   = '#506280'
const FAINT   = '#8EA4BC'
const GOLD    = '#2563eb'

function formatBytes(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function fileIcon(mimeType) {
  if (!mimeType) return File
  if (mimeType.includes('pdf'))        return FileText
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return FileSpreadsheet
  if (mimeType.startsWith('image/'))   return FileImage
  return FileText
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function FilesPage() {
  const [files,     setFiles]     = useState([])
  const [loading,   setLoading]   = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error,     setError]     = useState('')
  const [dragOver,  setDragOver]  = useState(false)
  const inputRef = useRef(null)

  const load = async () => {
    try {
      setLoading(true)
      const { data } = await api.get('/files')
      setFiles(data || [])
    } catch (err) {
      setError('Could not load files.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const upload = async (fileList) => {
    if (!fileList?.length) return
    setUploading(true)
    setError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      for (const file of Array.from(fileList)) {
        const form = new FormData()
        form.append('file', file)
        await fetch(
          `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/files/upload`,
          {
            method: 'POST',
            headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
            body: form,
          }
        )
      }
      await load()
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (fileId) => {
    try {
      await api.delete(`/files/${fileId}`)
      setFiles(prev => prev.filter(f => f.id !== fileId))
    } catch {
      setError('Could not delete file.')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    upload(e.dataTransfer.files)
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display" style={{ fontSize: '2rem', color: TEXT }}>My Files</h1>
          <p className="font-sans text-sm mt-1" style={{ color: FAINT }}>
            Upload PDFs, Word docs, or any supporting material for your research.
          </p>
        </div>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-sans text-sm font-semibold transition-all disabled:opacity-60"
          style={{ background: GOLD, color: '#FFFFFF' }}
          onMouseEnter={e => !uploading && (e.currentTarget.style.opacity = '0.9')}
          onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
        >
          {uploading ? <Loader2 style={{ width: 15, height: 15, animation: 'spin 1s linear infinite' }} /> : <Upload style={{ width: 15, height: 15 }} />}
          Upload File
        </button>
        <input ref={inputRef} type="file" className="hidden" multiple onChange={e => upload(e.target.files)} />
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl text-sm font-sans" style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
          {error}
        </div>
      )}

      {/* Drop zone */}
      <div
        className="rounded-2xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors cursor-pointer"
        style={{
          borderColor: dragOver ? GOLD : BORDER,
          background: dragOver ? 'rgba(37,99,235,0.04)' : BG,
        }}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <Upload style={{ width: 28, height: 28, color: dragOver ? GOLD : FAINT, marginBottom: 10 }} />
        <p className="font-sans font-medium text-sm" style={{ color: dragOver ? GOLD : MUTED }}>
          Drop files here or click to browse
        </p>
        <p className="font-sans text-xs mt-1" style={{ color: FAINT }}>PDF, DOCX, TXT, XLSX — up to 50 MB each</p>
      </div>

      {/* File list */}
      <div className="rounded-2xl shadow-card overflow-hidden" style={{ background: SURFACE, border: `1px solid ${BORDER}` }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
          <h2 className="font-sans font-semibold text-sm" style={{ color: TEXT }}>Uploaded Files</h2>
          {!loading && (
            <span className="font-mono text-xs" style={{ color: FAINT }}>{files.length} file{files.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 style={{ width: 24, height: 24, color: FAINT, animation: 'spin 1s linear infinite' }} />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-14">
            <FolderOpen style={{ width: 36, height: 36, color: FAINT, margin: '0 auto 12px', opacity: 0.5 }} />
            <p className="font-sans text-sm font-medium" style={{ color: MUTED }}>No files yet</p>
            <p className="font-sans text-xs mt-1" style={{ color: FAINT }}>Upload a file above to get started.</p>
          </div>
        ) : (
          <div className="divide-y" style={{ '--tw-divide-color': BORDER }}>
            {files.map(file => (
              <div key={file.id} className="flex items-center gap-4 px-6 py-3.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(37,99,235,0.08)' }}>
                  {(() => { const Icon = fileIcon(file.mime_type); return <Icon style={{ width: 16, height: 16, color: GOLD }} /> })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm font-medium truncate" style={{ color: TEXT }}>{file.name}</p>
                  <p className="font-mono text-xs mt-0.5" style={{ color: FAINT }}>
                    {formatBytes(file.size)} · {formatDate(file.created_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {file.url && (
                    <a href={file.url} download={file.name} target="_blank" rel="noreferrer"
                      className="p-2 rounded-lg transition-colors"
                      style={{ color: FAINT }}
                      onMouseEnter={e => e.currentTarget.style.color = TEXT}
                      onMouseLeave={e => e.currentTarget.style.color = FAINT}>
                      <Download style={{ width: 14, height: 14 }} />
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: FAINT }}
                    onMouseEnter={e => e.currentTarget.style.color = '#DC2626'}
                    onMouseLeave={e => e.currentTarget.style.color = FAINT}>
                    <Trash2 style={{ width: 14, height: 14 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
